import env from 'env'

import { uuid, wrap } from './utils'
import logger, { Logger } from './utils/logger'

import events, { EventBus, EventsTrait } from './events/bus'

import {
  SDK_AUTH_END,
  SDK_AUTH_ERROR,
  SDK_AUTH_START,
  SDK_AUTH_STATUS_CHANGE,
  SDK_CONCURRENT_PLAYBACK_START,
  SDK_CONFIGURATION_END,
  SDK_CONFIGURATION_ERROR,
  SDK_CONFIGURATION_START,
  SDK_STATE_ERROR,
  SDK_STATE_READY
} from './events/types/sdk.events'

import {
  initGoogleFirebase,
  preloadFirebaseScripts
} from './services/concurrency/firebase'

import { preloadAmplitudeScript } from './services/analytics/amplitude'

import { preloadHLS } from './utils/hls'

import { OidcClient } from './services/oidc'
import { PlaybackSessionConcurrency } from './services/concurrency'
import { ConfigurationError } from './errors'

import { Account } from './components/account'
import { PopupWindow } from './services/oidc/utils/navigator'
import amplitude, { AmplitudeAnalytics } from './services/analytics/amplitude'
import { AudioPlaybackTelemetry } from './services/telemetry/audio'
import { VideoPlaybackTelemetry } from './services/telemetry/video'
import { PLAYER_MODULE } from './constants'

type ClientAppConfig = {
  id: string
  name?: string,
  components?: Components
}

type PlaybackConfig = {
  showLeadingZero?: boolean,
  analytics: boolean
}

export type AuthOptions = {
  type?: 'popup' | 'redirect',
  workflow?: Workflow
  subscribe?: boolean,
  promocode?: string,
  url?: {
    pathname?: string,
    query?: string
  }
}


export type Workflow = 'register' | 'login'
export type ResponseMode = string | 'web_message' | 'query'
export type ResponseType = string | 'code' | 'token' | 'id_token' | 'id_token token' | 'code id_token' | 'code token' | 'code id_token token'
export type Components = {
  subscription?: boolean
  stash?: boolean
}

export type SDKConfig = {
  logger?: boolean
  version?: string
  workflow: Workflow
  callbackUrl?: string
  mode: ResponseMode
  silent: boolean,
  state?: 'permanent' | 'session'
  checkSession?: boolean
  app: ClientAppConfig
  playback?: PlaybackConfig
  amplitude?: boolean
}

const VERSION = '2.0'
const configKey = Symbol('config')

const p = {
  cdn: env.NUGSNET_CDN,
  service: env.SUBSCRIPTION_SERVICE
}

export function createAccount(oidc: OidcClient, config: SDKConfig) {
  const state = oidc?.state
  if (state && state.jwt) {
    return new Account(state.jwt.sub as string, state.jwt.email as string, {
      token: state.token?.access_token,
      legacy_token: state.jwt.legacy_token,
      legacy_uguid: state.jwt.legacy_uguid
    } as never, config)
  } else {
    return new Account('', '', {} as never, config)
  }
}

async function finishAuthorization(sdk: SDK, options: AuthOptions, popup?: PopupWindow) {
  if (!sdk.account.subscription.id || !sdk.account.stash.ready) await sdk.account.fetch()

  // If we ask to subscribe and user is not subscriber
  if (!sdk.account.isSubscriber ? !sdk.account.stash.hasContent || options.subscribe : false) {
    if (!popup) popup = new PopupWindow(p)
    // Open subscription service
    await wrap(popup.openSubscriptionService({
      promocode: options.promocode,
      url: options.url?.pathname,
      query: options.url?.query || ''
    }))

    popup && popup.close()

    // Check account
    await Promise.allSettled([
      sdk.account.fetch(),
      initGoogleFirebase()
    ])
  } else popup && popup.close()
}

type Telemetry = {
  video: VideoPlaybackTelemetry
  audio?: AudioPlaybackTelemetry
}

export interface BaseSDK {
  configure(applicationId: string, name: string, workflow?: Workflow): Promise<unknown>
  authorize(options?: AuthOptions): Promise<unknown>
  readonly uuid: string
  readonly account: Account
  readonly telemetry: Telemetry | undefined
  readonly config: SDKConfig
  readonly events: EventBus
  readonly version: string
  readonly logger: Logger
  readonly concurrency: PlaybackSessionConcurrency
  readonly oidc: OidcClient | undefined
  readonly isAuthorized: boolean
  readonly isReady: boolean
  readonly amplitude: AmplitudeAnalytics | undefined
  readonly isConfigured: boolean
  readonly isStateReady: Promise<unknown>
}

export type NugsSDK = BaseSDK

class SDK extends EventsTrait implements BaseSDK {
  [configKey]: SDKConfig
  #events = events
  #oidc?: OidcClient
  #isConfiguring: boolean
  #logger: Logger
  uuid: string
  #account!: Account
  #concurrency!: PlaybackSessionConcurrency
  #amplitude?: AmplitudeAnalytics
  #telemetry?: Telemetry

  static logger?: Logger

  static toString(): string {
    return '[class SDK]'
  }

  static get className() {
    return 'SDK'
  }

  get account() {
    return this.#account
  }

  get config(): SDKConfig {
    return this[configKey]
  }

  get events() {
    return this.#events
  }

  get version() {
    return (this[configKey as never] as SDKConfig).version || VERSION
  }

  get logger() {
    return this.#logger
  }

  get concurrency() {
    return this.#concurrency
  }

  get telemetry() {
    return this.#telemetry
  }

  get oidc() {
    return this.#oidc
  }

  get isAuthorized() {
    return Boolean(this.#oidc?.isAuthorized) && this.account.isAuthorized
  }

  get isReady() {
    return !!(this.isConfigured &&
      this.#oidc &&
      this.#oidc?.state &&
      this.#oidc.state.id &&
      this.#oidc.state.data)
  }

  get amplitude() {
    return this.#amplitude
  }

  get isConfigured() {
    return !this.#isConfiguring && !!this.#oidc && !!this.#account
  }

  get isStateReady(): Promise<boolean> {
    return new Promise(((resolve, reject) => {
      if (this.isReady) {
        return resolve(true)
      } else {
        this.on(SDK_STATE_READY.type, resolve)
        this.on(SDK_STATE_ERROR.type, reject)

        return this.isReady
      }
    }))
  }

  constructor(config: SDKConfig) {
    super()
    this.uuid = uuid()

    this.#isConfiguring = false
    this.#telemetry = Object.freeze({ video: new VideoPlaybackTelemetry(), audio: undefined })
    this.#logger = new Logger(!config.logger)

    this[configKey] = { ...config, version: VERSION } as SDKConfig

    SDK.logger = this.logger
    Account.logger = this.logger

    const log = this.logger.GREEN.OUTLINE

    this.#account = new Account(null as never, '', {} as never, this.config)

    if (this.config.amplitude) this.#amplitude = amplitude

    // Concurrent playback
    this.events.addEventListener(SDK_CONCURRENT_PLAYBACK_START, () => {
      if (this.account.isAuthorized && this.concurrency) {
        this.account.isAuthorized && log(PLAYER_MODULE, '[concurrency]', 'start')
        this.concurrency.start()
      }
    })
  }

  private async silentAuth() {
    const log = this.logger.BLUE.OUTLINE
    // Try to make silent login
    if (this.config.silent && this.oidc) {
      // If we dont have any active session...
      if (!this.oidc.isAuthorized) {
        this.events.dispatchEvent(SDK_AUTH_START)
        log('SDK', '[authorization:start]', { silent: this.config.silent })

        // ...just try to do silent login
        await wrap(this.oidc.silentSignIn())

        this.events.dispatchEvent(SDK_AUTH_END)
        log('SDK', '[authorization:end]', { silent: this.config.silent })
      }

      log(SDK.className, '[authorization:status]', this.isAuthorized)
    }
  }

  async configure() {
    const log = this.logger.BLUE.OUTLINE

    try {
      if (this.#isConfiguring) {
        return new Promise((resolve, reject) => {
          this.on(SDK_CONFIGURATION_END.type, resolve)
          this.on(SDK_CONFIGURATION_ERROR.type, reject)
        })
      } else if (this.isReady) return

      this.#isConfiguring = true

      log(SDK.className, '[configuration:start]')

      this.events.dispatchEvent(SDK_CONFIGURATION_START)

      await Promise.allSettled([
        preloadHLS(),
        preloadFirebaseScripts(),
        preloadAmplitudeScript(),
      ])

      const oidc = new OidcClient({
        authority: env.OIDC_AUTHORITY,
        clientId: this.config.app.id,
        callbackUrl: this.config.callbackUrl,
        origin: location.origin,
        responseMode: this.config.mode,
        workflow: this.config.workflow,
        state: this.config.state
      }, !!this.config.checkSession)

      this.#oidc = oidc

      await wrap(oidc.loadState())
      await this.silentAuth()

      this.#account = createAccount(this.#oidc, this.config)

      log(SDK.className, '[configuration:account]', { account: this.account })

      await Promise.allSettled([
        initGoogleFirebase(),
        this.amplitude?.init(),
        this.account.fetch()
      ].filter(Boolean))

      // Concurrent playback manager
      this.#concurrency = new PlaybackSessionConcurrency(+this.account.id, this.account.tokens.legacy_uguid)

      this.events.dispatchEvent(SDK_AUTH_END)
      this.events.dispatchEvent(SDK_STATE_READY)

      log(SDK.className, '[configuration:firebase]')
    } catch (error) {
      this.events.dispatchEvent(SDK_STATE_ERROR)
      this.events.dispatchEvent(SDK_CONFIGURATION_ERROR)

      this.logger.RED.OUTLINE(SDK.className, '[configuration:error]', { error })
    } finally {
      this.#isConfiguring = false

      this.events.dispatchEvent(SDK_CONFIGURATION_END)

      log(SDK.className, '[configuration:end]', { config: this.config, account: this.account, sdk: this })
    }
  }

  async fetchIdToken() {
    await this.isStateReady
    await this.oidc?.fetch()
  }

  public fetchToken() {
    return this.fetchIdToken()
  }

  public async signout() {
    await this.isStateReady
    await this.oidc?.signOut()
  }

  public async authorize(options: AuthOptions = { subscribe: true, workflow: this.config.workflow, type: 'popup' }) {
    try {
      if (!this.isConfigured || !this.oidc) {
        this.events.dispatchEvent(SDK_AUTH_ERROR)
        throw new ConfigurationError
      }

      await wrap(Promise.resolve(this.oidc.state?.load()))

      const workflow = options.workflow || this.config.workflow

      this.events.dispatchEvent(SDK_AUTH_START)

      if (options.type === 'popup' || !options.type) {
        // We're not authorized. Lets open popup.
        if (!this.isAuthorized && this.oidc) {
        // Sign in with popup
          const popup = await this.oidc.signInWithPopup(true, p, workflow) as never as PopupWindow

          if (this.oidc?.isAuthorized) {
            popup.showLoader(true)

            this.#account = createAccount(this.oidc, this.config)

            await finishAuthorization(this, options, popup)
          } else popup.close()
        } else {
        // User has already silently authorized
          await finishAuthorization(this, options)
        }
      } else if (options.type === 'redirect' && this.oidc && !this.isAuthorized) {
        await this.oidc.signInWithRedirect()
      }

      this.#concurrency = new PlaybackSessionConcurrency(+this.account.id, this.account.tokens.legacy_uguid)
      this.events.dispatchEvent(SDK_AUTH_STATUS_CHANGE(this.account.isAuthorized))
    } catch (error) {
      this.events.dispatchEvent(SDK_AUTH_ERROR)
      throw error
    } finally {
      this.events.dispatchEvent(SDK_AUTH_END)
    }
  }

  public async unauthorize() {
    if (!this.isConfigured) {
      this.events.dispatchEvent(SDK_AUTH_STATUS_CHANGE(this.isAuthorized))
      this.events.dispatchEvent(SDK_AUTH_ERROR)
      throw new ConfigurationError
    } else if (this.isAuthorized && this.oidc) {
      await wrap(this.oidc.signOut())
    }
  }
}

class WebSDK extends SDK {
  get VERSION() {
    return this.version
  }

  static get className() {
    return 'WebSDK'
  }

  get EVENTS() {
    return this.events
  }

  // @ts-expect-error: ignore
  async configure(applicationId: string, name = 'nugs.net SDK client', workflow?: Workflow) {
    if (workflow) this.config.workflow = workflow
    this.config.app = Object.freeze({ ...this.config.app, id: applicationId, name })

    await super.configure()

    this[configKey] = Object.freeze(this.config)
  }
}

export function create(config: SDKConfig) {
  return new WebSDK(config)
}

export default create({
  logger: !!env.LOG,
  playback: {
    analytics: true,
    showLeadingZero: false
  },
  callbackUrl: location.origin,
  silent: true,
  checkSession: true,
  app: {
    id: null as never,
    name: 'unknown'
  },
  state: 'session',
  mode: 'web_message',
  workflow: 'register',
  amplitude: !!env.AMPLITUDE
})

WebSDK.logger = SDK['logger'] || logger

export { SDK, WebSDK }
